import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { ArrowLeft, Share2, BadgeCheck, Star, MapPin, ExternalLink, Calendar, ShoppingBag, Heart, Flag, Store } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { resolveAssetUrl } from "@/lib/assetUrl";
import BusinessLogo from "@/components/BusinessLogo";
import SaveToBoardDialog from "@/components/SaveToBoardDialog";
import ReportPinDialog from "@/components/ReportPinDialog";
import FollowButton from "@/components/FollowButton";
import InstagramEmbedPlayer from "@/components/InstagramEmbedPlayer";
import { toast } from "sonner";

const IG_URL_RE = /instagram\.com\/(?:p|reel|tv)\//i;

const trackEvent = (pinId, eventType, metadata) => {
  axios
    .post(`${API}/blinterest/pins/${pinId}/events`, { event_type: eventType, metadata: metadata || null })
    .catch(() => {});
};

export default function PinDetailPage() {
  const { pinId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pin, setPin] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/blinterest/pins/${pinId}`)
      .then((r) => {
        setPin(r.data);
        // Phase 1 upgrade: keep the legacy `view` event for backwards
        // compatibility, plus a normalised `drop_view` funnel event.
        trackEvent(pinId, "view");
        trackEvent(pinId, "drop_view");
      })
      .catch(() => setPin(null))
      .finally(() => setLoading(false));
    axios
      .get(`${API}/blinterest/pins/${pinId}/related`)
      .then((r) => setRelated(r.data.pins || []))
      .catch(() => {});
  }, [pinId]);

  const onIgRender = useCallback(() => {
    trackEvent(pinId, "drop_video_play");
  }, [pinId]);

  const onSave = () => {
    if (!user) {
      navigate("/join?returnTo=" + encodeURIComponent(window.location.pathname));
      return;
    }
    setSaveOpen(true);
  };

  const onShare = async () => {
    const url = window.location.href;
    trackEvent(pinId, "share");
    trackEvent(pinId, "drop_share");
    if (navigator.share) {
      try {
        await navigator.share({ title: pin?.title, url });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Couldn't copy link");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-10 animate-pulse">
          <div className="h-96 bg-white/10 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!pin) {
    return (
      <div className="min-h-screen bg-tbc-navy">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-white">
          <h1 className="font-heading text-3xl mb-4">Find not found</h1>
          <Link to="/blinterest">
            <Button className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full">
              Back to Discover
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const biz = pin.business;
  const isInstagramSource = pin.source_url && IG_URL_RE.test(pin.source_url);
  // Server may set an explicit `cta_type` per Phase 1 upgrade. Otherwise we
  // fall back to the smart default (booking → product → destination) so old
  // pins keep working. `"none"` explicitly hides the secondary CTA.
  const ctaType = (pin.cta_type || "").toLowerCase();
  const hasBooking = !!pin.booking_url;
  const hasProduct = !!pin.product_url;
  const hasDestination = !!(pin.destination_url || biz?.website_url);
  let activeCta = ctaType;
  if (!activeCta) {
    if (hasBooking) activeCta = "book_now";
    else if (hasProduct) activeCta = "shop_product";
    else if (hasDestination) activeCta = "visit_website";
    else activeCta = "shop_business";
  }
  const trackCta = (label) => trackEvent(pin.pin_id, "drop_cta_click", { cta: label });

  return (
    <div className="min-h-screen bg-tbc-navy" data-testid="pin-detail-page">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link
          to="/blinterest"
          className="inline-flex items-center text-white/70 hover:text-white font-body text-sm"
          data-testid="back-to-blinterest"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Discover
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-5 gap-8">
        {/* Hero — inline Instagram reel/post for IG-sourced drops, else static image. */}
        <div className="lg:col-span-3">
          {isInstagramSource ? (
            <InstagramEmbedPlayer
              sourceUrl={pin.source_url}
              posterUrl={pin.image_url}
              title={pin.title}
              caption={pin.description}
              onRender={onIgRender}
            />
          ) : (
            <div className="rounded-2xl overflow-hidden bg-white/5">
              <img
                src={resolveAssetUrl(pin.image_url)}
                alt={pin.title}
                className="w-full h-auto object-cover"
                data-testid="pin-hero-image"
              />
            </div>
          )}
        </div>

        {/* Detail column */}
        <div className="lg:col-span-2 space-y-5">
          <div className="flex items-center gap-2">
            <Button
              onClick={onSave}
              className="flex-1 bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-11"
              data-testid="pin-save-btn"
            >
              <Heart className="w-4 h-4 mr-2" />
              Keep This Find
            </Button>
            <Button
              onClick={onShare}
              variant="outline"
              className="border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full h-11"
              data-testid="pin-share-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          <h1 className="font-heading text-3xl text-white" data-testid="pin-title">
            {pin.title}
          </h1>
          {pin.description && (
            <p className="text-white/75 font-body leading-relaxed">{pin.description}</p>
          )}

          {/* Report link (subtle) */}
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-1 text-white/40 hover:text-white/70 text-xs font-body underline decoration-dotted underline-offset-2"
            data-testid="pin-report-btn"
          >
            <Flag className="w-3 h-3" />
            Report this Find
          </button>

          {/* Business connection card — the core hook */}
          {biz && (
            <div
              className="rounded-2xl border border-tbc-gold/40 bg-white/5 p-4 space-y-3"
              data-testid="pin-business-card"
            >
              <p className="text-tbc-gold text-[10px] uppercase tracking-widest font-semibold">
                Available From Black-Owned Businesses
              </p>
              <div className="flex items-center gap-3">
                <BusinessLogo name={biz.name} logoUrl={biz.logo_url} size="md" shape="rounded" />
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-white text-base flex items-center gap-1 truncate">
                    {biz.name}
                    {biz.is_verified && <BadgeCheck className="w-4 h-4 text-tbc-gold flex-none" />}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-white/60 mt-0.5">
                    {(biz.bc_score || 0) > 0 && (
                      <span className="inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-tbc-gold fill-tbc-gold" />
                        {biz.bc_score?.toFixed(1)}
                      </span>
                    )}
                    {biz.city && biz.state && (
                      <span className="inline-flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" />
                        {biz.city}, {biz.state}
                      </span>
                    )}
                  </div>
                </div>
                <FollowButton
                  targetType="business"
                  targetId={biz.business_id}
                  variant="compact"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to={`/business/${biz.business_id}`}
                  onClick={() => { trackEvent(pin.pin_id, "business_click"); trackEvent(pin.pin_id, "drop_business_click"); }}
                  data-testid="pin-view-business-btn"
                >
                  <Button className="w-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full h-10 text-sm">
                    <Store className="w-4 h-4 mr-1" />
                    Shop This Business
                  </Button>
                </Link>
                {activeCta === "book_now" && pin.booking_url ? (
                  <a
                    href={pin.booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { trackEvent(pin.pin_id, "booking_click"); trackCta("book_now"); }}
                    data-testid="pin-book-btn"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full h-10 text-sm"
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Book Now
                    </Button>
                  </a>
                ) : activeCta === "shop_product" && pin.product_url ? (
                  <a
                    href={pin.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { trackEvent(pin.pin_id, "product_click"); trackCta("shop_product"); }}
                    data-testid="pin-shop-btn"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full h-10 text-sm"
                    >
                      <ShoppingBag className="w-4 h-4 mr-1" />
                      Shop the Look
                    </Button>
                  </a>
                ) : activeCta === "visit_website" && hasDestination ? (
                  <a
                    href={pin.destination_url || biz.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { trackEvent(pin.pin_id, "website_click"); trackCta("visit_website"); }}
                    data-testid="pin-visit-btn"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full h-10 text-sm"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Visit Site
                    </Button>
                  </a>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Related pins */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h2 className="font-heading text-2xl text-white mb-4">More Like This</h2>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4">
            {related.map((p) => (
              <Link
                key={p.pin_id}
                to={`/blinterest/pin/${p.pin_id}`}
                className="mb-4 block break-inside-avoid rounded-2xl overflow-hidden bg-white/5 border border-white/5 hover:border-tbc-gold/40"
                data-testid={`related-pin-${p.pin_id}`}
              >
                <img
                  src={resolveAssetUrl(p.image_url)}
                  alt={p.title}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="p-3">
                  <p className="text-white font-body text-sm line-clamp-2">{p.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />

      <SaveToBoardDialog
        pin={pin}
        open={saveOpen}
        onOpenChange={setSaveOpen}
      />

      <ReportPinDialog
        pinId={pin.pin_id}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
    </div>
  );
}
